class ProductComparator {
	constructor(containerId, datasets, options = {}) {
		this.containerId = containerId;
		this.datasets = Array.isArray(datasets) ? datasets : [{ name: 'Default', values: datasets }];
		this.options = {
			maxValue: options.maxValue || 100,
			minValue: options.minValue || 0,
			gridLevels: options.gridLevels || 5,
			showAxisLines: options.showAxisLines !== false,
			showDataPoints: options.showDataPoints !== false,
			showGrid: options.showGrid !== false,
			animationDuration: options.animationDuration || 300,
			colors: {
				primary: options.colors?.primary || '#3F51B5',
				fill: options.colors?.fill || 'rgba(63, 81, 181, 0.25)',
				grid: options.colors?.grid || 'rgba(224, 224, 224, 0.6)',
				...options.colors,
			},
		};

		this.container = null;
		this.svg = null;
		this.tooltip = null;
		this.labels = [];
		this.values = [];
		this.numAxes = 0;
		this.polygonGroup = null;
		this.pointGroup = null;

		this.init();
	}

	init() {
		try {
			this.container = document.querySelector(`#${this.containerId}`);
			if (!this.container) {
				throw new Error(`Container avec l'ID "${this.containerId}" non trouvé`);
			}

			this.svg = this.container.querySelector('.comparator-svg');
			if (!this.svg) {
				throw new Error('Élément SVG non trouvé dans le container');
			}

			this.processData();
			this.render();
		} catch (error) {
			this.showError(`Erreur d'initialisation: ${error.message}`);
		}
	}

	processData() {
		if (
			!this.datasets ||
			this.datasets.length === 0 ||
			!this.datasets[0] ||
			!this.datasets[0].values
		) {
			// Valid state for an empty chart, do not throw an error.
			this.labels = [];
			this.numAxes = 0;
			this.datasets = []; // Normalize to ensure it's a clean array.
			return;
		}

		// Utiliser les clés du premier dataset comme référence
		const originalKeys = Object.keys(this.datasets[0].values);
		this.labels = originalKeys.map((key) => this.translateLabel(key));
		this.numAxes = this.labels.length;

		if (this.numAxes < 3) {
			throw new Error('Au moins 3 axes sont requis pour créer un graphique radar');
		}

		// Valider les valeurs de chaque dataset (utiliser les clés originales pour le lookup)
		this.datasets.forEach((dataset) => {
			dataset.values = originalKeys.map((key) => {
				const value = dataset.values[key];
				const numValue = parseFloat(value);
				if (isNaN(numValue)) {
					throw new Error(`Valeur invalide détectée: ${value}`);
				}
				return Math.max(this.options.minValue, Math.min(this.options.maxValue, numValue));
			});
		});
	}

	render() {
		this.clearSVG();
		this.createTooltip();

		const size = 300;
		const center = size / 2;
		const radius = center - 40;

		console.log('window.location.href', window.location.href);
		// 1. Dessiner les éléments de fond (toujours affichés)
		if (this.options.showAxisLines) {
			// Si pas de données, dessiner 6 axes par défaut
			let axesCount = this.numAxes > 0 ? this.numAxes : 6;
			// si on est sur la page balles, on dessine 4 axes par défaut
			if (window.location.href.includes('balles')) {
				axesCount = this.numAxes > 0 ? this.numAxes : 4;
			}
			this.drawAxisLines(center, radius, axesCount);
		}
		if (this.options.showGrid) {
			// Si pas de données, dessiner 6 axes par défaut
			let axesCount = this.numAxes > 0 ? this.numAxes : 6;
			// si on est sur la page balles, on dessine 4 axes par défaut
			if (window.location.href.includes('balles')) {
				axesCount = this.numAxes > 0 ? this.numAxes : 4;
			}
			this.drawGrid(center, radius, axesCount);
		}

		// Dessiner les labels par défaut si aucune donnée
		if (this.numAxes === 0) {
			// Les labels par défaut seront maintenant définis dans la section Liquid
			this.drawDefaultLabels(center, radius + 10, this.getDefaultLabels());
		} else {
			this.drawLabels(center, radius + 10);
		}

		// Ne dessiner les données que si elles existent
		if (this.datasets && this.datasets.length > 0 && this.numAxes > 0) {
			// 2. Créer des groupes pour contrôler la superposition
			this.polygonGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
			this.svg.appendChild(this.polygonGroup);

			this.pointGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
			this.svg.appendChild(this.pointGroup);

			const allDataPoints = this.datasets.map((dataset) =>
				this.calculateDataPoints(dataset.values, center, radius),
			);

			// 3. Dessiner toutes les formes de données dans leur groupe
			allDataPoints.forEach((dataPoints, index) => {
				const color = this.getDatasetColor(index);
				const fillColor = this.getDatasetColor(index, 0.2);
				this.drawDataShape(dataPoints, color, fillColor, index);
			});

			// 4. Dessiner tous les points de données dans leur groupe
			if (this.options.showDataPoints) {
				allDataPoints.forEach((dataPoints, index) => {
					const color = this.getDatasetColor(index);
					this.drawDataPoints(dataPoints, color, index);
				});
			}

			// 5. Ajouter les animations
			this.addAnimations();
		}
	}

	clearSVG() {
		while (this.svg.firstChild) {
			this.svg.removeChild(this.svg.firstChild);
		}
	}

	drawAxisLines(center, radius, axesCount = null) {
		const numAxes = axesCount || this.numAxes;
		for (let i = 0; i < numAxes; i++) {
			const angle = ((Math.PI * 2) / numAxes) * i - Math.PI / 2;
			const x = center + radius * Math.cos(angle);
			const y = center + radius * Math.sin(angle);

			const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
			line.setAttribute('x1', center);
			line.setAttribute('y1', center);
			line.setAttribute('x2', x);
			line.setAttribute('y2', y);
			line.setAttribute('class', 'comparator-axis-line');
			this.svg.appendChild(line);
		}
	}

	drawGrid(center, radius, axesCount = null) {
		const numAxes = axesCount || this.numAxes;
		for (let i = 1; i <= this.options.gridLevels; i++) {
			const gridPolygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
			let points = '';

			for (let j = 0; j < numAxes; j++) {
				const angle = ((Math.PI * 2) / numAxes) * j - Math.PI / 2;
				const r = radius * (i / this.options.gridLevels);
				const x = center + r * Math.cos(angle);
				const y = center + r * Math.sin(angle);
				points += `${x},${y} `;
			}

			gridPolygon.setAttribute('points', points.trim());
			gridPolygon.setAttribute('class', 'comparator-grid-line');
			this.svg.appendChild(gridPolygon);
		}

		// Ajouter l'échelle de référence
		this.drawReferenceScale(center, radius);
	}

	/**
	 * Dessine l'échelle de référence avec les valeurs numériques
	 */
	drawReferenceScale(center, radius) {
		// Créer un groupe pour l'échelle
		const scaleGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
		scaleGroup.setAttribute('class', 'comparator-reference-scale');

		// Dessiner les valeurs de 10 en 10 le long du premier axe (vers le haut)
		for (let value = 10; value <= this.options.maxValue; value += 10) {
			// Calculer la position relative (0-1) pour cette valeur
			const normalizedValue = value / this.options.maxValue;
			const r = radius * normalizedValue;

			// Position le long du premier axe (angle -π/2 = vers le haut)
			const angle = -Math.PI / 2;
			const x = center + r * Math.cos(angle);
			const y = center + r * Math.sin(angle);

			// Créer le texte de la valeur au centre de l'arête
			const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
			text.setAttribute('x', x);
			text.setAttribute('y', y);
			text.textContent = value;
			text.setAttribute('class', 'comparator-scale-value');

			scaleGroup.appendChild(text);
		}

		this.svg.appendChild(scaleGroup);
	}

	calculateDataPoints(values, center, radius) {
		return values.map((value, i) => {
			const angle = ((Math.PI * 2) / this.numAxes) * i - Math.PI / 2;
			const normalizedValue =
				(value - this.options.minValue) / (this.options.maxValue - this.options.minValue);
			const currentRadius = normalizedValue * radius;

			return {
				x: center + currentRadius * Math.cos(angle),
				y: center + currentRadius * Math.sin(angle),
				value: value,
				label: this.labels[i],
			};
		});
	}

	drawDataShape(dataPoints, color, fillColor, index) {
		const dataShape = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
		const pointsString = dataPoints.map((p) => `${p.x},${p.y}`).join(' ');

		dataShape.setAttribute('points', pointsString);
		dataShape.setAttribute('class', 'comparator-data-shape');
		dataShape.setAttribute('fill', fillColor);
		dataShape.setAttribute('stroke', color);
		dataShape.setAttribute('data-dataset-index', index);

		this.polygonGroup.appendChild(dataShape);
	}

	drawDataPoints(dataPoints, color, index) {
		dataPoints.forEach((point) => {
			const pointCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
			pointCircle.setAttribute('cx', point.x);
			pointCircle.setAttribute('cy', point.y);

			// Détecter si on est sur mobile et ajuster la taille du rayon
			const isMobile = window.innerWidth <= 480;
			const radius = isMobile ? 4.5 : 3.5;
			const hoverRadius = isMobile ? 5 : 4;
			pointCircle.setAttribute('r', radius);

			pointCircle.setAttribute('class', 'comparator-data-point');
			pointCircle.setAttribute('stroke', color);
			pointCircle.setAttribute('fill', 'white');

			// Stocker les rayons pour les événements hover
			pointCircle.setAttribute('data-radius', radius);
			pointCircle.setAttribute('data-hover-radius', hoverRadius);

			// Ajouter un data attribute pour l'interactivité
			pointCircle.setAttribute('data-label', point.label);
			pointCircle.setAttribute('data-value', point.value);
			pointCircle.setAttribute('data-dataset-index', index);

			// Calculer le pourcentage
			const percentage = Math.round((point.value / this.options.maxValue) * 100);
			pointCircle.setAttribute('data-percentage', percentage);

			// Ajouter les événements de survol
			this.addHoverEvents(pointCircle, point, percentage);

			this.pointGroup.appendChild(pointCircle);
		});
	}

	drawLabels(center, labelRadius) {
		this.labels.forEach((label, i) => {
			const angle = ((Math.PI * 2) / this.numAxes) * i - Math.PI / 2;

			// Ajuster la distance selon la position du label
			let adjustedRadius = labelRadius;

			// Pour un carré (4 côtés), augmenter l'espacement pour tous les labels
			if (this.numAxes === 4) {
				adjustedRadius = labelRadius + 10;
			}
			// Pour un hexagone (6 côtés), identifier les positions problématiques
			else if (this.numAxes === 6) {
				// Position 1 (Confort), 4 (Tolérance) et 5 (Maniabilité) ont besoin de plus d'espace
				if (i === 1 || i === 4 || i === 5) {
					adjustedRadius = labelRadius + 15;
				} else if (i === 2) {
					adjustedRadius = labelRadius + 10;
				} else if (i === 3 || i === 0) {
					adjustedRadius = labelRadius + 0;
				}
			}

			const x = center + adjustedRadius * Math.cos(angle);
			const y = center + adjustedRadius * Math.sin(angle);

			const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
			text.setAttribute('x', x);
			text.setAttribute('y', y);
			text.textContent = label;
			text.setAttribute('class', 'comparator-label');

			// Ajuster la position pour éviter les chevauchements
			this.adjustLabelPosition(text, angle, x, y);

			this.svg.appendChild(text);
		});
	}

	adjustLabelPosition(textElement, angle, x, y) {
		// Ajuster l'ancrage du texte selon la position
		if (angle > Math.PI / 4 && angle < (3 * Math.PI) / 4) {
			textElement.setAttribute('text-anchor', 'start');
		} else if (angle > (-3 * Math.PI) / 4 && angle < -Math.PI / 4) {
			textElement.setAttribute('text-anchor', 'end');
		} else {
			textElement.setAttribute('text-anchor', 'middle');
		}
	}

	addAnimations() {
		const dataShapes = this.polygonGroup.querySelectorAll('.comparator-data-shape');
		const dataPoints = this.pointGroup.querySelectorAll('.comparator-data-point');

		if (dataShapes.length > 0) {
			dataShapes.forEach((shape) => {
				shape.style.opacity = '0';
				shape.style.transform = 'scale(0)';
				shape.style.transformOrigin = 'center';

				// Animation d'entrée
				setTimeout(() => {
					shape.style.transition = `all ${this.options.animationDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
					shape.style.opacity = '1';
					shape.style.transform = 'scale(1)';
				}, 100);
			});
		}

		// Animation des points avec délai échelonné
		dataPoints.forEach((point, index) => {
			point.style.opacity = '0';
			point.style.transform = 'scale(0)';
			point.style.transformOrigin = 'center';

			setTimeout(
				() => {
					point.style.transition = `all ${this.options.animationDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
					point.style.opacity = '1';
					point.style.transform = 'scale(1)';
				},
				200 + index * 50,
			);
		});
	}

	createTooltip() {
		if (this.tooltip) return;

		// Créer le nouveau tooltip
		this.tooltip = document.createElement('div');
		this.tooltip.className = 'comparator-tooltip';
		this.container.appendChild(this.tooltip);
	}

	addHoverEvents(pointElement, point, percentage) {
		pointElement.addEventListener('mouseenter', (e) => {
			// Agrandir le point au survol (compatible iOS)
			const hoverRadius = pointElement.getAttribute('data-hover-radius');
			pointElement.setAttribute('r', hoverRadius);

			this.showTooltip(e, point, percentage);
		});

		pointElement.addEventListener('mouseleave', () => {
			// Remettre la taille normale (compatible iOS)
			const normalRadius = pointElement.getAttribute('data-radius');
			pointElement.setAttribute('r', normalRadius);

			this.hideTooltip();
		});

		pointElement.addEventListener('mousemove', (e) => {
			this.updateTooltipPosition(e);
		});
	}

	showTooltip(event, point, percentage) {
		if (!this.tooltip) return;

		// Récupérer l'index du dataset survolé
		const hoveredDatasetIndex = parseInt(event.target.getAttribute('data-dataset-index'));

		// Créer la liste des produits pour la comparaison
		const comparisonItems = [];

		// Ajouter le produit survolé en premier
		if (this.datasets[hoveredDatasetIndex]) {
			const hoveredDataset = this.datasets[hoveredDatasetIndex];
			const hoveredValue = hoveredDataset.values[this.labels.indexOf(point.label)];
			const hoveredPercentage = Math.round((hoveredValue / this.options.maxValue) * 100);
			const hoveredColor = this.getDatasetColor(hoveredDatasetIndex);

			comparisonItems.push({
				name: hoveredDataset.name,
				value: hoveredValue,
				percentage: hoveredPercentage,
				color: hoveredColor,
				isHovered: true,
			});
		}

		// Ajouter les autres produits
		this.datasets.forEach((dataset, index) => {
			if (index !== hoveredDatasetIndex) {
				const value = dataset.values[this.labels.indexOf(point.label)];
				const percentage = Math.round((value / this.options.maxValue) * 100);
				const color = this.getDatasetColor(index);

				comparisonItems.push({
					name: dataset.name,
					value: value,
					percentage: percentage,
					color: color,
					isHovered: false,
				});
			}
		});

		// Générer le HTML de la popup
		let tooltipHTML = `<div class="tooltip-header">${point.label}</div>`;

		comparisonItems.forEach((item) => {
			const itemClass = item.isHovered ? 'tooltip-item tooltip-item--hovered' : 'tooltip-item';
			tooltipHTML += `
				<div class="${itemClass}">
					<div class="tooltip-item-color" style="background-color: ${item.color}"></div>
					<div class="tooltip-item-name">${item.name}</div>
					<div class="tooltip-item-value">${item.value}</div>
				</div>
			`;
		});

		this.tooltip.innerHTML = tooltipHTML;
		this.tooltip.classList.add('show');
		this.updateTooltipPosition(event);
	}

	hideTooltip() {
		if (!this.tooltip) return;
		this.tooltip.classList.remove('show');
	}

	updateTooltipPosition(event) {
		if (!this.tooltip) return;

		const containerRect = this.container.getBoundingClientRect();
		const tooltipRect = this.tooltip.getBoundingClientRect();
		const isMobile = window.innerWidth <= 768;

		// Position de base
		let left = event.clientX - containerRect.left;
		let top = event.clientY - containerRect.top - tooltipRect.height - 15;

		// Centrer horizontalement par rapport au curseur
		left = left - tooltipRect.width / 2;

		// Ajustements pour mobile
		if (isMobile) {
			const margin = 10;
			const viewportWidth = window.innerWidth;
			const viewportHeight = window.innerHeight;

			// Calcul de la position absolue sur la page
			const absoluteLeft = containerRect.left + left;
			const absoluteTop = containerRect.top + top;

			// Ajustement horizontal pour mobile
			if (absoluteLeft < margin) {
				left = margin - containerRect.left;
			} else if (absoluteLeft + tooltipRect.width > viewportWidth - margin) {
				left = viewportWidth - margin - tooltipRect.width - containerRect.left;
			}

			// Ajustement vertical pour mobile
			if (absoluteTop < margin) {
				// Si pas de place au-dessus, placer en dessous
				top = event.clientY - containerRect.top + 20;
			}

			// Vérifier si la popup dépasse en bas
			if (containerRect.top + top + tooltipRect.height > viewportHeight - margin) {
				top = viewportHeight - margin - tooltipRect.height - containerRect.top;
			}
		} else {
			// Ajustements pour desktop (logique existante)
			if (left + tooltipRect.width > containerRect.width) {
				left = containerRect.width - tooltipRect.width - 10;
			}
			if (left < 10) {
				left = 10;
			}
			if (top < 10) {
				top = event.clientY - containerRect.top + 20;
			}
		}

		this.tooltip.style.left = left + 'px';
		this.tooltip.style.top = top + 'px';
	}

	showError(message) {
		if (this.container) {
			this.container.innerHTML = `<div class="comparator-error">${message}</div>`;
		} else {
			console.error('ProductComparator:', message);
		}
	}

	/**
	 * Traduit une clé de metafield via le mapping data-label-translations
	 */
	translateLabel(key) {
		if (!this._labelTranslations) {
			this._labelTranslations = {};
			const el = document.querySelector('[data-label-translations]');
			if (el) {
				try {
					this._labelTranslations = JSON.parse(el.textContent);
				} catch (e) {
					// ignore parse errors
				}
			}
		}
		return this._labelTranslations[key] || key;
	}

	/**
	 * Récupère les labels par défaut depuis les data attributes ou utilise des valeurs de base
	 */
	getDefaultLabels() {
		let defaultLabelsElement;

		if (window.location.href.includes('balles')) {		
			defaultLabelsElement = document.querySelector('[data-default-labels-balls]');
		} else {
			defaultLabelsElement = document.querySelector('[data-default-labels]');
		}
			
		if (defaultLabelsElement) {
			try {
				return JSON.parse(defaultLabelsElement.textContent);
			} catch (e) {
				console.warn('Impossible de parser les labels par défaut depuis le DOM');
			}
		}

		// Valeurs de fallback si rien n'est trouvé (traduit via le mapping si disponible)
		return ['Puissance', 'Contrôle', 'Confort', 'Spin', 'Tolérance', 'Maniabilité'].map(
			(key) => this.translateLabel(key)
		);
	}

	// Méthode pour mettre à jour les données
	update(newDatasets) {
		// Validation des données avant mise à jour
		if (newDatasets && newDatasets.length > 0) {
			// Vérifier que tous les datasets ont la même structure
			const referenceKeys = Object.keys(newDatasets[0].values || {}).sort();
			const hasValidStructure = newDatasets.every((dataset) => {
				if (!dataset.values || typeof dataset.values !== 'object') return false;
				const keys = Object.keys(dataset.values).sort();
				return (
					keys.length === referenceKeys.length &&
					keys.every((key, index) => key === referenceKeys[index])
				);
			});

			if (!hasValidStructure) {
				console.warn('ProductComparator: Incompatible data structures detected, ignoring update');
				return;
			}
		}

		this.datasets = Array.isArray(newDatasets)
			? newDatasets
			: [{ name: 'Default', values: newDatasets }];
		this.processData();
		this.render();
	}

	// Méthode pour surligner un dataset
	highlightDataset(datasetIndex) {
		if (typeof datasetIndex === 'undefined') return;

		const polygons = this.polygonGroup.querySelectorAll('.comparator-data-shape');
		if (!polygons) return;

		polygons.forEach((polygon) => {
			const polygonIndex = parseInt(polygon.getAttribute('data-dataset-index'), 10);
			if (polygonIndex === datasetIndex) {
				// Augmenter l'opacité et mettre en avant
				const highlightedColor = this.getDatasetColor(polygonIndex, 0.75);
				polygon.setAttribute('fill', highlightedColor);
				this.polygonGroup.appendChild(polygon); // Mettre au premier plan (dans son groupe)
			} else {
				// Estomper les autres
				const fadedColor = this.getDatasetColor(polygonIndex, 0.1);
				polygon.setAttribute('fill', fadedColor);
			}
		});

		const points = this.pointGroup.querySelectorAll('.comparator-data-point');
		if (!points) return;

		points.forEach((point) => {
			const pointIndex = parseInt(point.getAttribute('data-dataset-index'), 10);
			if (pointIndex === datasetIndex) {
				point.style.opacity = '1';
			} else {
				point.style.opacity = '0.2';
			}
		});
	}

	// Méthode pour annuler le surlignage
	clearHighlight() {
		const polygons = this.polygonGroup.querySelectorAll('.comparator-data-shape');
		if (!polygons) return;

		const polygonsArray = Array.from(polygons);

		// Trier les polygones par leur index d'origine
		polygonsArray.sort((a, b) => {
			const indexA = parseInt(a.getAttribute('data-dataset-index'), 10);
			const indexB = parseInt(b.getAttribute('data-dataset-index'), 10);
			return indexA - indexB;
		});

		// Ré-appliquer dans le bon ordre et réinitialiser le remplissage
		polygonsArray.forEach((polygon, index) => {
			const originalColor = this.getDatasetColor(index, 0.2); // Opacité par défaut
			polygon.setAttribute('fill', originalColor);
			this.polygonGroup.appendChild(polygon); // Ceci les ré-ordonne dans le DOM (du groupe)
		});

		const points = this.pointGroup.querySelectorAll('.comparator-data-point');
		if (!points) return;

		points.forEach((point) => {
			point.style.opacity = '1';
		});
	}

	// Méthode pour changer les options
	updateOptions(newOptions) {
		this.options = { ...this.options, ...newOptions };
		this.render();
	}

	getDatasetColor(index, opacity = 1) {
		const colorSet = Object.keys(this.options.colors)
			.filter((key) => key.startsWith('set'))
			.sort((a, b) => {
				const numA = parseInt(a.replace('set', ''), 10);
				const numB = parseInt(b.replace('set', ''), 10);
				return numA - numB;
			})
			.map((key) => this.options.colors[key]);

		let hexColor;

		if (colorSet.length > 0) {
			hexColor = colorSet[index % colorSet.length];
		} else {
			hexColor = this.options.colors.primary || '#3F51B5';
		}

		if (opacity < 1) {
			return this.hexToRgba(hexColor, opacity);
		}
		return hexColor;
	}

	hexToRgba(hex, opacity) {
		if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
			let c = hex.substring(1).split('');
			if (c.length === 3) {
				c = [c[0], c[0], c[1], c[1], c[2], c[2]];
			}
			c = `0x${c.join('')}`;
			return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',')},${opacity})`;
		}
		// Retourne une couleur de secours si le format est invalide
		return `rgba(63, 81, 181, ${opacity})`;
	}

	drawDefaultLabels(center, labelRadius, labels) {
		labels.forEach((label, i) => {
			const angle = ((Math.PI * 2) / labels.length) * i - Math.PI / 2;

			// Ajuster la distance selon la position du label
			let adjustedRadius = labelRadius;

			// Pour un carré (4 côtés), augmenter l'espacement pour tous les labels
			if (labels.length === 4) {
				adjustedRadius = labelRadius + 10;
			}
			// Pour un hexagone (6 côtés), identifier les positions problématiques
			else if (labels.length === 6) {
				// Position 1 (Confort), 4 (Tolérance) et 5 (Maniabilité) ont besoin de plus d'espace
				if (i === 1 || i === 4 || i === 5) {
					adjustedRadius = labelRadius + 8;
				}
			}

			const x = center + adjustedRadius * Math.cos(angle);
			const y = center + adjustedRadius * Math.sin(angle);

			const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
			text.setAttribute('x', x);
			text.setAttribute('y', y);
			text.textContent = label;
			text.setAttribute('class', 'comparator-label');

			// Ajuster la position pour éviter les chevauchements
			this.adjustLabelPosition(text, angle, x, y);

			this.svg.appendChild(text);
		});
	}
}

// Fonction d'initialisation globale pour Shopify
function initProductComparator(containerId, data, options = {}) {
	try {
		// If data is null or undefined, pass an empty array to the constructor.
		const initialDataset = data ? [{ name: 'Initial', values: data }] : [];
		return new ProductComparator(containerId, initialDataset, options);
	} catch (error) {
		console.error(`Erreur lors de l'initialisation du ProductComparator: ${error.message}`);
		const container = document.querySelector(`#${containerId}`);
		if (container) {
			container.innerHTML = `<div class="comparator-error">${error.message}</div>`;
		}
		return null;
	}
}

// Export pour utilisation moderne
if (typeof module !== 'undefined' && module.exports) {
	module.exports = { ProductComparator, initProductComparator };
}

// Global pour compatibilité Shopify
window.ProductComparator = ProductComparator;
window.initProductComparator = initProductComparator;
